import React, { useState, useEffect, useCallback } from "react"; // Added useCallback
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField'; // Import TextField
import Autocomplete from '@mui/material/Autocomplete'; // Import Autocomplete
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { motion } from 'framer-motion';
import Theme from './Theme'; 
import PropTypes from "prop-types"
import IconButton from '@mui/material/IconButton'; // For potential icon buttons
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Example icon
import RefreshIcon from '@mui/icons-material/Refresh'; // Example icon
import CircularProgress from '@mui/material/CircularProgress'; // Import CircularProgress
import Alert from '@mui/material/Alert'; // Import Alert for error messages
import * as Turbo from "@hotwired/turbo"; // Explicit Turbo import
import Snackbar from '@mui/material/Snackbar'; // Import Snackbar
import Dialog from '@mui/material/Dialog'; // Import Dialog
import DialogActions from '@mui/material/DialogActions'; // Import DialogActions
import DialogContent from '@mui/material/DialogContent'; // Import DialogContent
import DialogContentText from '@mui/material/DialogContentText'; // Import DialogContentText
import DialogTitle from '@mui/material/DialogTitle';
import OnboardingStepper from './OnboardingStepper'; // Import the stepper

// Helper to get CSRF token
const getCsrfToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  return token;
};

// --- Main Settings Page Component ---
// Renders the second step of onboarding: Selecting Xero Accounts.
const OnboardingSettingsPage = ({ 
  currentCompany, // Keep currentCompany prop
}) => {
  // State for accounts from API
  const [apiAccounts, setApiAccounts] = useState([]);
  const [isAccountsLoading, setIsAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [profitAccountId, setProfitAccountId] = useState('');
  const [feesAccountId, setFeesAccountId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showNoAccountsModal, setShowNoAccountsModal] = useState(false); // State for modal

  // Fetch accounts on initial mount and when company changes
  const fetchAccounts = useCallback(async () => {
    if (!currentCompany?.id) return; // Don't fetch if no company ID

    setIsAccountsLoading(true);
    setAccountsError(null);
    const accountsUrl = `/api/internal/companies/${currentCompany.id}/accounts`;
    const csrfToken = getCsrfToken(); // Needed if API requires auth/CSRF for GET

    try {
      const response = await fetch(accountsUrl, {
        headers: {
          'Accept': 'application/json',
          // Add CSRF if needed for GET requests (depends on Rails config)
           ...(csrfToken && { 'X-CSRF-Token': csrfToken }) 
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setApiAccounts(data || []); // Ensure it's an array
    } catch (err) {
      console.error("Failed to fetch accounts:", err);
      setAccountsError(`Failed to load accounts: ${err.message}`);
      setApiAccounts([]); // Clear accounts on error
    } finally {
      setIsAccountsLoading(false);
    }
  }, [currentCompany?.id]);

  // Fetch accounts on initial mount and when company changes
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Check for empty accounts after fetch completes
  useEffect(() => {
    if (!isAccountsLoading && !accountsError && apiAccounts.length === 0) {
      // Avoid showing modal immediately if a refresh just failed (let snackbar show)
      if (!isRefreshing) { 
         setShowNoAccountsModal(true);
      }
    }
  }, [isAccountsLoading, accountsError, apiAccounts, isRefreshing]); // Add isRefreshing dependency

  // Initialize form state from currentCompany metadata (depends on apiAccounts being loaded)
  useEffect(() => {
    // Only initialize if accounts are loaded and we haven't set state yet
    if (!isAccountsLoading && currentCompany?.metadata) {
       // Check if state is still initial ('') before overwriting
       if (profitAccountId === '') {
         setProfitAccountId(currentCompany.metadata.profit_account_id || '');
       }
       if (feesAccountId === '') {
         setFeesAccountId(currentCompany.metadata.fee_account_id || '');
       }
    }
    // Depend on metadata and accounts loading state
  }, [currentCompany?.metadata, isAccountsLoading, profitAccountId, feesAccountId]); 

  // Find the account object based on the stored ID for the Autocomplete value prop
  const findAccountById = (id) => apiAccounts.find(acc => acc.id === id) || null;

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCloseNoAccountsModal = () => {
    setShowNoAccountsModal(false);
  };

  const handleSyncFromModal = () => {
    setShowNoAccountsModal(false); // Close modal first
    handleRefreshAccounts(); // Then trigger refresh
  };

  // Mock handlers (replace with actual logic later)
  const handleProfitChange = (event, newValue) => {
    // newValue is the selected account object or null
    setProfitAccountId(newValue ? newValue.id : '');
    setSaveError(null); // Clear error on change
  };
  const handleFeesChange = (event, newValue) => {
    // newValue is the selected account object or null
    setFeesAccountId(newValue ? newValue.id : '');
    setSaveError(null); // Clear error on change
  };
  const handleRefreshAccounts = async () => {
    console.log("Refreshing Xero accounts...");
    setIsRefreshing(true);
    setSnackbarOpen(false);
    const csrfToken = getCsrfToken();

    if (!currentCompany?.id) {
      setSnackbarMessage("Company ID is missing. Cannot refresh accounts.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsRefreshing(false);
      return;
    }
    if (!csrfToken) {
      setSnackbarMessage("CSRF token missing. Please refresh the page.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsRefreshing(false);
      return;
    }

    const refreshUrl = `/api/internal/companies/${currentCompany.id}/refresh_xero_accounts`;

    try {
      const response = await fetch(refreshUrl, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });

      const responseData = await response.json(); // Always try to parse JSON

      if (!response.ok) {
        throw new Error(responseData.error || `HTTP error! status: ${response.status}`);
      }
      
      // Success: Show message and THEN fetch updated accounts
      setSnackbarMessage(responseData.message || "Accounts synchronized successfully!");
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      await fetchAccounts(); // Fetch updated accounts list after sync
      
    } catch (err) {
      console.error("Failed to refresh accounts:", err);
      setSnackbarMessage(`Refresh failed: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsRefreshing(false); 
    }
  };
  const handleContinue = async () => {
    setSaveError(null);
    setIsSaving(true);
    setSnackbarOpen(false);
    const csrfToken = getCsrfToken();

    if (!currentCompany?.id) {
      setSaveError("Company ID is missing. Cannot save settings.");
      setIsSaving(false);
      return;
    }

    if (!csrfToken) {
      setSaveError("CSRF token not found. Please refresh the page.");
      setIsSaving(false);
      return;
    }

    const payload = {
      company: {
        metadata: {
          profit_account_id: profitAccountId,
          fee_account_id: feesAccountId,
        }
      }
    };

    const targetUrl = `/api/internal/companies/${currentCompany.id}`; // Construct URL here

    try {
      const response = await fetch(targetUrl, { // Use constructed URL
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.join(', ') || `HTTP error! status: ${response.status}`);
      }

      // Success - Navigate to the next step (wallets) using Turbo
      const currentSearchParams = new URLSearchParams(window.location.search);
      currentSearchParams.set('step', 'wallets'); 
      const nextUrl = `/onboarding?${currentSearchParams.toString()}`;
      Turbo.visit(nextUrl); 

    } catch (err) {
      console.error("Failed to update company settings:", err);
      setSaveError(`Failed to save settings: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  const handleBack = () => {
    // Use Turbo to navigate back, preserving params but setting step=company
    const currentSearchParams = new URLSearchParams(window.location.search);
    currentSearchParams.set('step', 'company');
    const backUrl = `/onboarding?${currentSearchParams.toString()}`;
    if (typeof Turbo !== 'undefined') {
      Turbo.visit(backUrl);
    } else {
      // Fallback or handle differently if Turbo isn't available
      window.location.href = backUrl;
    }
  };

  // Define options for Autocomplete, handling empty/null accounts
  const accountOptions = apiAccounts || [];
  const isContinueDisabled = !profitAccountId || !feesAccountId || isSaving || isRefreshing || isAccountsLoading || apiAccounts.length === 0;
  const isRefreshDisabled = isSaving || isRefreshing || isAccountsLoading;

  return (
    <Theme>
      <React.Fragment>
        {/* Snackbar for feedback */} 
        <Snackbar 
          open={snackbarOpen} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }} // Position at top-center
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* No Accounts Found Modal */} 
        <Dialog
          open={showNoAccountsModal}
          onClose={handleCloseNoAccountsModal} 
          aria-labelledby="no-accounts-dialog-title"
          aria-describedby="no-accounts-dialog-description"
        >
          <DialogTitle id="no-accounts-dialog-title">
            No Accounts Found
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="no-accounts-dialog-description">
              We couldn't find any synchronized accounts. This might mean there are no accounts in your Xero chart of accounts, or they haven't been synchronized yet.
              <br /><br />
              Would you like to try synchronizing with Xero now?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNoAccountsModal}>Later</Button>
            <Button onClick={handleSyncFromModal} variant="contained" autoFocus>
              {isRefreshing ? <CircularProgress size={20} sx={{mr: 1}} /> : null} {/* Show loader in button */} 
              Sync Now
            </Button>
          </DialogActions>
        </Dialog>

        <Grid container component="main" sx={{ height: '100vh' }}>
          {/* Left Panel */}
          <Grid
          size={5}
          item
          sx={{
            background:
              'linear-gradient(211.21deg, #3376C2 29.32%, #2B62A1 95.41%)',
            position: 'relative',
            overflow: 'hidden',
            display: { xs: 'none', md: 'block' }
          }}
        >
            <Box
              position="absolute"
              top={108}
              left={59}
              component={motion.div}
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, type: 'spring', bounce: 0.25 }}
              zIndex={1}
            >
              <img src="/logo-large.png" alt="Contacryto" width={258} height={66} />
            <Typography
              variant="h4"
              mt={4}
              sx={{ color: 'common.white', maxWidth: '30rem' }}
            >
              Confirm your company information

            </Typography>
            <Typography
              variant="h5"
              mt={4}
              sx={{ color: 'common.white', maxWidth: '30rem' }}
            >
              Select Xero Accounts

            </Typography>
            </Box>
            <Box
              position="absolute"
              right={0}
              bottom={-4}
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9, x: -600, y: 300 }}
              animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
              transition={{ duration: 1, type: 'spring', bounce: 0.25 }}
            >
              {/* <LinesComponent /> */}
          </Box>
          </Grid>

          {/* Right Panel (Settings Form) */}
          <Grid
            item // Removed size prop
            sx={{
              height: '100vh',
              overflowY: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '2rem',
              flexGrow: 1
            }}
          >
            <OnboardingStepper activeStep={2} />

            {/* Inner container */}
            <Box 
              sx={{ 
                width: '100%', 
                maxWidth: '600px', 
                mx: 'auto', 
                px: 3, 
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1 
              }}
            >
              {/* Top Controls */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button 
                  startIcon={<ArrowBackIcon />} 
                  onClick={handleBack} 
                  size="small"
                  disabled={isAccountsLoading || isSaving || isRefreshing}
                >
                  Back
                </Button>
                <Button 
                  startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                  onClick={handleRefreshAccounts} 
                  size="small"
                  variant="outlined"
                  disabled={isRefreshDisabled} 
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh Accounts'}
                </Button>
              </Box>

              {/* Titles and Instructions */}
              <Typography variant="h4" component="h1" gutterBottom>
                Contacrypto
              </Typography>
              <Typography variant="h5" component="h2" mb={1}>
                Select Xero Accounts
              </Typography>
              <Typography variant="caption" display="block" sx={{mb: 1}}>
                 2/3
              </Typography>
               <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
                 Please use the dropdown menu to select the corresponding Xero account number. If you can't find the account, you will have to create it in Xero in the "Chart of accounts" page, then come back and refresh the page.
               </Typography>

              {/* Loading / Error states for Accounts */}
              {isAccountsLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 2 }} />}
              {accountsError && !showNoAccountsModal && <Alert severity="error" sx={{ mb: 2 }}>{accountsError}</Alert>}
              {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

              {/* Form Fields Stack - Render if not loading accounts, no fetch error OR modal isn't showing */} 
              {!isAccountsLoading && (!accountsError || showNoAccountsModal) && (
                <Stack spacing={4} sx={{ width: '100%', mb: 4, mt: (accountsError && !showNoAccountsModal) ? 2 : 0 }}> {/* Add margin top if fetch error is shown */} 
                  {/* Show Autocomplete only if accounts exist */} 
                  {apiAccounts.length > 0 ? (
                    <>
                      <Autocomplete /* Profit Account */
                        id="profit-account-autocomplete"
                        options={accountOptions} 
                        getOptionLabel={(option) => `${option.code} - ${option.name}` || ''}
                        value={findAccountById(profitAccountId)}
                        onChange={handleProfitChange}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        disabled={isSaving || isRefreshing || isAccountsLoading}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Profit in crypto" 
                            variant="standard" 
                            error={!!saveError}
                          />
                        )}
                      />
                      <Autocomplete /* Fees Account */
                        id="fees-account-autocomplete"
                        options={accountOptions} 
                        getOptionLabel={(option) => `${option.code} - ${option.name}` || ''}
                        value={findAccountById(feesAccountId)}
                        onChange={handleFeesChange}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        disabled={isSaving || isRefreshing || isAccountsLoading}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Crypto fees" 
                            variant="standard" 
                            error={!!saveError}
                          />
                        )}
                      />
                    </>
                  ) : (
                    // Show message only if modal isn't active and loading is done
                    !showNoAccountsModal && !isAccountsLoading && 
                    <Typography sx={{textAlign: 'center', mt: 2, color: 'text.secondary'}}>
                      No accounts available. Try refreshing accounts from Xero.
                    </Typography> 
                  )}
                </Stack>
              )}
              
              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} /> 

              {/* Action Buttons */}
              <Stack spacing={2} sx={{ width: '100%', mt: 'auto' }}>
                <Button
                  onClick={handleContinue}
                  fullWidth
                  variant="contained"
                  disabled={isContinueDisabled}
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isSaving ? 'Saving...' : 'Continue'} 
                </Button>
                {/* Optional Skip Link - Consider behavior carefully */}
                {/* <Link href="#" variant="body2" sx={{ alignSelf: 'center' }}>
                  Skip
                </Link> */}
              </Stack>

              {/* Footer */}
              <Typography variant="caption" display="block" sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
                 Copyright © Contacrypto 2025
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </React.Fragment>
    </Theme>
  );
}

// Update PropTypes
OnboardingSettingsPage.propTypes = {
  currentCompany: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    metadata: PropTypes.shape({
      profit_account_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fee_account_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  }), 
};

// Default props
OnboardingSettingsPage.defaultProps = {
  currentCompany: { metadata: {} }, // Provide default shape
};

// Update the export
export default OnboardingSettingsPage;
